import { forwardRef, useCallback, useRef, useState } from "react";
import { IconLoader2 } from "@tabler/icons-react";
import { useTranslation } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { observer } from "mobx-react";

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * Optional async callback that runs when the user clicks confirm. While it
   * is in flight, the buttons are disabled and a spinner is shown. On success
   * the dialog closes and `confirm()` resolves to `true`. On rejection the
   * dialog stays open so the caller can surface an error and let the user
   * retry; the error propagates back to `confirm()`'s awaiter via rejection.
   *
   * If not provided, the dialog closes synchronously when confirm is clicked.
   */
  onConfirm?: () => Promise<void> | void;
}

export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const rejecterRef = useRef<((reason: unknown) => void) | null>(null);

  const confirm = useCallback((nextOptions: ConfirmOptions) => {
    setOptions(nextOptions);
    setIsOpen(true);
    return new Promise<boolean>((resolve, reject) => {
      resolverRef.current = resolve;
      rejecterRef.current = reject;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    rejecterRef.current = null;
    setIsOpen(false);
    setIsSubmitting(false);
  }, []);

  const cancel = useCallback(() => {
    if (isSubmitting) return; // Ignore cancel attempts mid-submit
    settle(false);
  }, [isSubmitting, settle]);

  const accept = useCallback(async () => {
    const onConfirm = options?.onConfirm;
    if (!onConfirm) {
      settle(true);
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm();
      settle(true);
    } catch (error) {
      // Keep dialog open; caller handles messaging and may retry.
      setIsSubmitting(false);
      rejecterRef.current?.(error);
      resolverRef.current = null;
      rejecterRef.current = null;
    }
  }, [options, settle]);

  const ConfirmDialogNode = (
    <ConfirmDialog
      isOpen={isOpen}
      options={options}
      isSubmitting={isSubmitting}
      onCancel={cancel}
      onAccept={accept}
    />
  );

  return { confirm, ConfirmDialog: ConfirmDialogNode };
}

interface ConfirmDialogProps {
  isOpen: boolean;
  options: ConfirmOptions | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onAccept: () => void;
  /** `alertdialog` for a prompt that interrupts other work (the discard prompt). */
  role?: "dialog" | "alertdialog";
  /** Opened over another dialog: draws its own backdrop (see `DialogContent`). */
  stacked?: boolean;
}

/** Exported for `discard-changes-prompt.tsx`, which needs the same explicit-choice modal
 *  driven by a guard's state rather than by `useConfirmDialog`'s promise. */
export const ConfirmDialog = observer(
  forwardRef<HTMLDivElement, ConfirmDialogProps>(function ConfirmDialog(
    // Defaulted, never left undefined: an explicit `role={undefined}` reaching the popup
    // overrides base-ui's own `role="dialog"`, and every confirmation loses its role.
    { isOpen, options, isSubmitting, onCancel, onAccept, role = "dialog", stacked = false },
    ref,
  ) {
    const { t } = useTranslation();
    if (!options) return null;

    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open, details) => {
          if (open || isSubmitting) return;
          // Confirmation modals require an explicit choice: they are not
          // dismissible by Escape or an outside click (only the Cancel /
          // Confirm buttons settle them). Cancel those close attempts so a
          // stray keypress can't silently abort a destructive action.
          if (details.reason === "escape-key" || details.reason === "outside-press") {
            details.cancel();
            return;
          }
          onCancel();
        }}
      >
        <DialogContent
          ref={ref}
          role={role}
          stacked={stacked}
          data-slot="confirm-dialog"
          className="sm:max-w-md"
          showCloseButton={false}
        >
          <DialogHeader>
            <DialogTitle>{options.title}</DialogTitle>
            {options.description ? (
              <DialogDescription>{options.description}</DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              {options.cancelLabel ?? t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant={options.destructive ? "destructive" : "default"}
              onClick={onAccept}
              disabled={isSubmitting}
            >
              {isSubmitting && <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />}
              {options.confirmLabel ?? t("common.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }),
);
ConfirmDialog.displayName = "ConfirmDialog";
